import * as React from 'react';
import * as ReactDom from 'react-dom';
import './styles.css';
import { apiBaseUrl, appVersion, getActiveClient, getToken, viewAsHeaders, initAuth, IPortalMe, portalCall, Provider, publicConfig } from './auth';
import { LogoMark } from './brand';
import { navigate, Shell } from './Shell';
import Landing from './Landing';
import SignIn from './SignIn';
import Portal from './Portal';
import { ClientsAdmin } from './Admin';
import { TeamAdmin } from './People';
import Invite from './Invite';

// app.beefedupit.com
//   /                  public home page (no sign-in)          /privacy, /terms  static pages
//   /signin            Microsoft 365 or Google Workspace
//   /apps              launcher: apps enabled for the user's organization
//   /production        BatchWorks (loaded on demand; /seltzer is the original link)
//   /invite            invitation link (shows the invite, then signs in and accepts)
//   /admin/team        people & roles               /admin/clients   platform owner

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SeltzerApp = React.lazy(async (): Promise<{ default: React.ComponentType<any> }> => {
  const [{ default: App }, { SeltzerApiService }] = await Promise.all([
    import('../../src/webparts/seltzerCalculator/SeltzerCalculatorApp'),
    import('../../src/webparts/seltzerCalculator/SeltzerApiService')
  ]);
  const svc = new SeltzerApiService(apiBaseUrl, getToken, getActiveClient, viewAsHeaders);
  return { default: (): React.ReactElement => <App service={svc} version={appVersion} /> };
});

const usePath = (): string => {
  const [path, setPath] = React.useState<string>(window.location.pathname);
  React.useEffect((): (() => void) => {
    const on = (): void => setPath(window.location.pathname);
    window.addEventListener('popstate', on);
    return (): void => window.removeEventListener('popstate', on);
  }, []);
  return path.replace(/\/+$/, '') || '/';
};

const Boot: React.FunctionComponent = () => (
  <div className="bu-boot"><div style={{ animation: 'bu-pulse 1.4s ease-in-out infinite' }}><LogoMark size={56} /></div>
    <style>{'@keyframes bu-pulse { 0%,100% { opacity: .55; transform: scale(.94); } 50% { opacity: 1; transform: scale(1); } }'}</style>
  </div>
);

/** Navigates after render (never during it). */
const Redirect: React.FunctionComponent<{ to: string }> = ({ to }) => {
  React.useEffect((): void => { navigate(to); }, [to]);
  return <Boot />;
};

const Root: React.FunctionComponent<{ provider: Provider | null; googleClientId: string | null; authError?: string }> = (props) => {
  const path: string = usePath();
  const signedIn: boolean = props.provider !== null;
  const [me, setMe] = React.useState<IPortalMe | null>(null);
  const [error, setError] = React.useState<string>('');

  React.useEffect((): void => {
    if (signedIn) { portalCall<IPortalMe>('GET', 'me').then(setMe).catch((e: Error): void => setError(e.message)); }
  }, [signedIn]);

  React.useEffect((): void => {
    document.title = path.indexOf('/seltzer') === 0 || path.indexOf('/production') === 0 ? 'BatchWorks · BeefedUp Apps' : path.indexOf('/admin') === 0 ? 'Admin · BeefedUp Apps' : 'BeefedUp Apps';
  }, [path]);

  const returnParam: string = new URLSearchParams(window.location.search).get('return') || '/apps';

  // Public pages
  if (path === '/') { return <Landing signedIn={signedIn} />; }
  if (path === '/signin') {
    if (signedIn) { return <Redirect to={returnParam} />; }
    return <SignIn googleClientId={props.googleClientId} error={props.authError} returnTo={returnParam} />;
  }

  if (path === '/invite') { return <Invite signedIn={signedIn} googleClientId={props.googleClientId} />; }

  // Everything else needs a session
  if (!signedIn) { return <Redirect to={`/signin?return=${encodeURIComponent(path)}`} />; }
  if (error !== '') { return <SignIn googleClientId={props.googleClientId} error={`We couldn't open your workspace: ${error}`} returnTo={path} />; }
  if (me === null) { return <Boot />; }

  if (path === '/admin/clients') { return <ClientsAdmin me={me} />; }
  if (path === '/admin' || path === '/admin/team') { return <TeamAdmin me={me} />; }

  // /production (and the original /seltzer link) open the Production app.
  if ((path.indexOf('/production') === 0 || path.indexOf('/seltzer') === 0) && me.apps.filter((a): boolean => a.key === 'seltzer').length > 0) {
    return (
      <Shell me={me} crumb="BatchWorks" active="apps">
        <React.Suspense fallback={<Boot />}><SeltzerApp /></React.Suspense>
      </Shell>
    );
  }
  return <Portal me={me} />;
};

// A client's Microsoft 365 admin lands here after approving the consent link - no sign-in needed.
const consentPage = (): boolean => {
  if (window.location.pathname.replace(/\/+$/, '') !== '/consent-complete') { return false; }
  const q: URLSearchParams = new URLSearchParams(window.location.search);
  const granted: boolean = q.get('admin_consent') === 'True';
  const detail: string = (q.get('error_description') || 'Please try the link again or contact BeefedUp IT.').replace(/</g, '&lt;');
  const root: HTMLElement | null = document.getElementById('root');
  if (root !== null) {
    root.innerHTML = `<div class="bu-boot"><div style="max-width:440px;text-align:center;color:#fff;font-family:Manrope,sans-serif;padding:24px">
      <h1 style="font-family:Sora,sans-serif;font-weight:600">${granted ? 'BeefedUp Apps is approved' : 'Consent was not completed'}</h1>
      <p style="color:#b3b8bf;line-height:1.6">${granted ? 'Your organization is connected. Your team can now sign in.' : detail}</p>
      <a href="/signin" style="display:inline-block;margin-top:12px;background:#a8191f;color:#fff;padding:12px 20px;border-radius:10px;text-decoration:none;font-weight:700">Go to sign in</a></div></div>`;
  }
  return true;
};

if (!consentPage()) {
  const el: HTMLElement | null = document.getElementById('root');
  ReactDom.render(<Boot />, el);
  Promise.all([initAuth(), publicConfig()])
    .then(([auth, cfg]): void => {
      ReactDom.render(<Root provider={auth.provider} googleClientId={cfg.google ? cfg.google.clientId : null} authError={auth.error} />, el);
    })
    .catch((e: Error): void => {
      console.error(e);
      ReactDom.render(<Root provider={null} googleClientId={null} authError={`Sign-in failed: ${e.message}`} />, el);
    });
}
